import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, TextField, CircularProgress, Alert, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import axios from 'axios';
import { checkParticipantExists, getParticipantById2, fetchAgeGroups, AgeGroup, API_BASE_URL } from '../api/contests';

const fakeUser = {
  id: 15,
  first_name: "Some",
  last_name: "One",
  username: "someone",
  language_code: "en",
  is_premium: true,
  allows_write_to_pm: true
};

type LinkData = {
  link: string;
  description?: string;
};

type ParticipantData = {
  age?: number;
  age_group_id?: number;
  weight?: number;
  info?: string;
  links?: LinkData[];
  name?: string;
};


const tg = (window as any)?.Telegram?.WebApp;
const telegramUser = tg?.initDataUnsafe?.user ?? fakeUser;
const id = telegramUser.id;

const TelegramWebUserProfile: React.FC = () => {
  const [userExists, setUserExists] = useState(false);
  const [participantData, setParticipantData] = useState<ParticipantData>({
    age: undefined,
    age_group_id: 0,
    weight: undefined,
    info: '',
    links: [{ link: '', description: '' }]
  });
  const [loading, setLoading] = useState(true);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [submissionStatus, setSubmissionStatus] = useState<{ success: boolean; message: string } | null>(null);

  // Fetch age groups
  useEffect(() => {
    fetchAgeGroups()
      .then(response => setAgeGroups(response))
      .catch(error => console.error('Error fetching age groups:', error));
  }, []);

  // Check if the user exists and fetch their profile data
  useEffect(() => {
    const loadData = async () => {
      try {
        const exists = await checkParticipantExists(id);
        setUserExists(exists);
        if (exists) {
          const data: ParticipantData = await getParticipantById2(id);
          // Handle null values
          setParticipantData({
            age: data.age ?? undefined,
            age_group_id: data.age_group_id ?? 0,
            weight: data.weight ?? undefined,
            info: data.info ?? '',
            links: data.links ?? [{ link: '', description: '' }],
            name: data.name ?? ''
          });
        }
      } catch (error) {
        console.error('Error loading participant data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Sanitize text input to prevent script injection
  const sanitizeInput = (input: string) => {
    const div = document.createElement('div');
    div.textContent = input;
    return div.innerHTML;
  };

  const handleInputChange = (field: keyof ParticipantData, value: any) => {
    setParticipantData(prev => ({
      ...prev,
      [field]: value === '' ? undefined : value
    }));
  };


  const handleLinkChange = (index: number, field: keyof LinkData, value: string) => {
    setParticipantData(prev => ({
      ...prev,
      links: prev.links?.map((link, i) => i === index ? { ...link, [field]: sanitizeInput(value) } : link)
    }));
  };

  const addLinkField = () => {
    if ((participantData?.links?.length ?? 0) < 3) {
      setParticipantData(prev => ({
        ...prev,
        links: [...(prev.links ?? []), { link: '', description: '' }]
      }));
    }
  };

  const removeLinkField = (index: number) => {
    setParticipantData(prev => ({
      ...prev,
      links: prev.links?.filter((_, i) => i !== index) ?? []
    }));
  };

  const handleSubmit = async () => {
    // Flatten the data before sending
    const formattedData = {
      age: participantData.age,
      age_group_id: participantData.age_group_id,
      weight: participantData.weight,
      info: {
        info: participantData.info || '',
        links: participantData.links || []
      }
    };

    // Convert the flattened structure to a JSON string to match the database format
    const payload = {
      age: formattedData.age,
      age_group_id: formattedData.age_group_id,
      weight: formattedData.weight,
      info: JSON.stringify({
        info: formattedData.info.info,
        links: formattedData.info.links
      })
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/update-participant/${id}`, payload);

      if (response.data.success) {
        setSubmissionStatus({ success: true, message: 'Профиль участника успешно обновлён!' });
      } else {
        setSubmissionStatus({ success: false, message: response.data.message });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setSubmissionStatus({ success: false, message: 'Произошла ошибка, попробуйте ещё раз.' });
    }
  };



  if (loading) {
    return <CircularProgress />;
  }

  return !userExists ? (
    <Alert severity="warning" sx={{ mb: 2 }}>
      Для доступа к этой странице необходимо принять участие в зарубах.
    </Alert>
  ) : (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Профиль участника {participantData.name}
      </Typography>
      <Typography variant="h6">
        (ни одно поле не является обязательным для заполнения)
      </Typography>
      {submissionStatus && (
        <Alert severity={submissionStatus.success ? 'success' : 'error'} sx={{ mb: 2 }}>
          {submissionStatus.message}
        </Alert>
      )}

      <TextField
        label="Возраст"
        type="number"
        variant="outlined"
        fullWidth
        margin="normal"
        value={participantData.age !== undefined ? participantData.age : ''}
        onChange={e => handleInputChange('age', e.target.value === '' ? undefined : Number(e.target.value))}
      />

      <FormControl fullWidth margin="normal">
        <InputLabel>Возрастная группа</InputLabel>
        <Select
          value={participantData.age_group_id}
          onChange={e => handleInputChange('age_group_id', Number(e.target.value))}
          label="Возрастная Группа"
        >
          {ageGroups.map(group => (
            <MenuItem key={group.age_group_id} value={group.age_group_id}>
              {group.age_group_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Вес"
        type="number"
        variant="outlined"
        fullWidth
        margin="normal"
        value={participantData.weight !== undefined ? participantData.weight : ''}
        onChange={e => handleInputChange('weight', e.target.value === '' ? undefined : Number(e.target.value))}
      />

      <TextField
        label="О себе"
        variant="outlined"
        fullWidth
        margin="normal"
        multiline
        rows={4}
        value={sanitizeInput(participantData.info ?? '')}
        inputProps={{ maxLength: 600 }}
        helperText={`${participantData.info?.length ?? 0}/500 знаков`}
        onChange={e => handleInputChange('info', sanitizeInput(e.target.value))}
      />

      <Typography variant="h6" gutterBottom>
        Ссылки участника
      </Typography>
      {participantData.links?.map((link, index) => (
        <Box key={index} mb={2}>
          <TextField
            label="Ссылка"
            variant="outlined"
            fullWidth
            margin="normal"
            value={link.link}
            onChange={e => handleLinkChange(index, 'link', e.target.value)}
          />
          <TextField
            label="Описание"
            variant="outlined"
            fullWidth
            margin="normal"
            value={sanitizeInput(link.description ?? '')}
            onChange={e => handleLinkChange(index, 'description', e.target.value)}
          />
          {(participantData?.links?.length ?? 0) > 1 && (
            <Button onClick={() => removeLinkField(index)} color="secondary">
              Удалить ссылку
            </Button>
          )}
        </Box>
      ))}

      {(participantData?.links?.length ?? 0) < 3 && (
        <Button onClick={addLinkField} color="primary">
          Добавить ссылку
        </Button>
      )}
      {submissionStatus && (
        <Alert severity={submissionStatus.success ? 'success' : 'error'} sx={{ mb: 2 }}>
          {submissionStatus.message}
        </Alert>
      )}
      <Box mt={3}>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Сохранить изменения
        </Button>
      </Box>
    </Box>
  );
};

export default TelegramWebUserProfile;
